import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Video,
  Clock,
  Calendar,
  ArrowLeft,
  ExternalLink,
  Radio,
  BookOpen,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { meetingsApi, MeetingData } from '../lib/api';
import { useAuthStore } from '../store/auth';
import toast from 'react-hot-toast';

import { joinMeetingSecure, buildJitsiJoinUrl } from '../lib/jitsi';

export default function MeetingRoomPage() {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [meeting, setMeeting] = useState<MeetingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [canJoin, setCanJoin] = useState(false);
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    const fetchMeeting = async () => {
      if (!meetingId) return;

      try {
        const res = await meetingsApi.getById(meetingId);
        if (res.data.success && res.data.data) {
          setMeeting(res.data.data);
          // Check if jitsiRoomId is available (means user can join)
          setCanJoin(!!res.data.data.jitsiRoomId);
        }
      } catch (error) {
        console.error('Failed to fetch meeting:', error);
        toast.error('Meeting not found');
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMeeting();
  }, [meetingId, navigate]);

  // Countdown timer
  useEffect(() => {
    if (!meeting || meeting.status !== 'SCHEDULED') return;

    const updateCountdown = () => {
      const now = new Date();
      const meetingTime = new Date(meeting.scheduledAt);
      const diff = meetingTime.getTime() - now.getTime();

      if (diff <= 0) {
        setCountdown('Starting soon...');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (days > 0) {
        setCountdown(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setCountdown(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setCountdown(`${minutes}m ${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [meeting]);

  const handleJoinMeeting = async () => {
    if (!meeting?.id) {
      toast.error('Unable to join meeting');
      return;
    }

    try {
      await joinMeetingSecure(meeting.id);
    } catch {
      // Fallback to direct URL if JWT fails
      if (meeting.jitsiRoomId) {
        window.open(buildJitsiJoinUrl(meeting.jitsiRoomId), '_blank');
      } else {
        toast.error('Unable to join meeting');
      }
    }
  };

  const isProfessor = user?.id === meeting?.professorId;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-coral/30 border-t-coral rounded-full animate-spin" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-screen bg-primary flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-coral mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Meeting Not Found</h2>
          <p className="text-white/50 mb-6">This meeting doesn't exist or you don't have access.</p>
          <Link to="/" className="btn-primary">Go Home</Link>
        </div>
      </div>
    );
  }

  const meetingDate = new Date(meeting.scheduledAt);
  const isLive = meeting.status === 'LIVE';
  const isEnded = meeting.status === 'ENDED';
  const isCancelled = meeting.status === 'CANCELLED';

  return (
    <div className="min-h-screen bg-primary">
      {/* Header */}
      <div className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/50 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Live Banner */}
        {isLive && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 rounded-2xl bg-gradient-to-r from-red-500/20 to-purple-500/20 border border-red-500/30 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center animate-pulse">
                <Radio className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-white">This session is live now!</p>
                <p className="text-sm text-white/60">Click the button below to join</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Meeting Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(15, 16, 20, 0.98) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          {/* Course Thumbnail */}
          <div className="h-48 bg-gradient-to-br from-purple-500/20 to-blue-500/20 relative">
            {meeting.course.thumbnailUrl ? (
              <img 
                src={meeting.course.thumbnailUrl} 
                alt={meeting.course.title}
                className="w-full h-full object-cover opacity-60"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Video className="w-20 h-20 text-white/20" />
              </div>
            )}
            
            {/* Status Badge */}
            <div className="absolute top-4 right-4">
              <span className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                isLive
                  ? 'bg-red-500 text-white'
                  : isEnded
                  ? 'bg-white/20 text-white/60'
                  : isCancelled
                  ? 'bg-white/10 text-white/40'
                  : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
              }`}>
                {isLive && <Radio className="w-4 h-4 animate-pulse" />}
                {isLive ? 'Live Now' : isEnded ? 'Ended' : isCancelled ? 'Cancelled' : 'Scheduled'}
              </span>
            </div>
            
            {/* Meeting Type Badge */}
            {meeting.meetingType === 'QA_FINALE' && (
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30">
                  Q&A Session
                </span>
              </div>
            )}
          </div>

          <div className="p-8">
            {/* Title */}
            <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">{meeting.title}</h1>
            
            {/* Course Link */}
            <Link 
              to={`/courses/${meeting.course.slug || meeting.course.id}`}
              className="text-white/50 hover:text-coral transition-colors flex items-center gap-2 mb-6"
            >
              <BookOpen className="w-4 h-4" />
              {meeting.course.title}
            </Link>

            {/* Description */}
            {meeting.description && (
              <p className="text-white/60 mb-6">{meeting.description}</p>
            )}

            {/* Details Grid */}
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <div className="p-4 rounded-xl bg-white/5">
                <div className="flex items-center gap-2 text-white/40 mb-2">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">Date</span>
                </div>
                <p className="font-semibold text-white">
                  {meetingDate.toLocaleDateString('en-US', { 
                    weekday: 'long',
                    month: 'long', 
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>
              
              <div className="p-4 rounded-xl bg-white/5">
                <div className="flex items-center gap-2 text-white/40 mb-2">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Time</span>
                </div>
                <p className="font-semibold text-white">
                  {meetingDate.toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    timeZoneName: 'short'
                  })}
                </p>
              </div>
              
              <div className="p-4 rounded-xl bg-white/5">
                <div className="flex items-center gap-2 text-white/40 mb-2">
                  <Video className="w-4 h-4" />
                  <span className="text-sm">Duration</span>
                </div>
                <p className="font-semibold text-white">{meeting.duration} minutes</p>
              </div>
            </div>

            {/* Professor Info */}
            <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 mb-8">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                {meeting.professor.avatarUrl ? (
                  <img 
                    src={meeting.professor.avatarUrl} 
                    alt={meeting.professor.firstName}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  `${meeting.professor.firstName[0]}${meeting.professor.lastName[0]}`
                )}
              </div>
              <div>
                <p className="text-sm text-white/50">Hosted by</p>
                <p className="font-semibold text-white">
                  {meeting.professor.firstName} {meeting.professor.lastName}
                </p>
              </div>
            </div>

            {/* Countdown or Action */}
            {meeting.status === 'SCHEDULED' && (
              <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 mb-6">
                <p className="text-white/50 mb-2">Session starts in</p>
                <p className="text-4xl font-bold text-white">{countdown}</p>
              </div>
            )}

            {/* Action Buttons */}
            {!isCancelled && !isEnded && (
              <div className="flex flex-col sm:flex-row gap-4">
                {canJoin ? (
                  <>
                    {isLive ? (
                      // Anyone can join a live meeting
                      <button
                        onClick={handleJoinMeeting}
                        className="flex-1 py-4 px-6 rounded-xl font-semibold flex items-center justify-center gap-3 transition-all bg-red-500 hover:bg-red-600 text-white"
                      >
                        <Radio className="w-5 h-5 animate-pulse" />
                        Join Live Session
                      </button>
                    ) : isProfessor ? (
                      // Only professor can start the meeting
                      <button
                        onClick={async () => {
                          try {
                            await meetingsApi.start(meeting.id);
                            toast.success('Meeting started!');
                            handleJoinMeeting();
                            // Refresh meeting data
                            const res = await meetingsApi.getById(meeting.id);
                            if (res.data.success && res.data.data) {
                              setMeeting(res.data.data);
                            }
                          } catch {
                            toast.error('Failed to start meeting');
                          }
                        }}
                        className="flex-1 py-4 px-6 rounded-xl font-semibold flex items-center justify-center gap-3 transition-all bg-emerald-500 hover:bg-emerald-600 text-white"
                      >
                        <Video className="w-5 h-5" />
                        Start Session
                      </button>
                    ) : (
                      // Students see waiting message for scheduled meetings
                      <div className="flex-1 py-4 px-6 rounded-xl bg-white/5 border border-white/10 text-center">
                        <div className="flex items-center justify-center gap-3 text-white/60">
                          <Clock className="w-5 h-5" />
                          <span>Waiting for professor to start the session</span>
                        </div>
                        <p className="text-sm text-white/40 mt-2">
                          You'll be able to join once the professor starts the meeting
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex-1 py-4 px-6 rounded-xl bg-white/5 text-white/40 text-center">
                    You don't have access to this meeting
                  </div>
                )}
              </div>
            )}

            {/* Ended Notice */}
            {isEnded && (
              <div className="p-4 rounded-xl bg-white/5 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <span className="text-white/60">This session has ended</span>
                {meeting.recordingUrl && (
                  <a 
                    href={meeting.recordingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto text-coral hover:text-coral/80 flex items-center gap-1"
                  >
                    Watch Recording
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}

            {/* Cancelled Notice */}
            {isCancelled && (
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-300">This session has been cancelled</span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Tips */}
        {meeting.status === 'SCHEDULED' && canJoin && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8 p-6 rounded-2xl bg-white/[0.02] border border-white/10"
          >
            <h3 className="font-semibold text-white mb-4">Before joining</h3>
            <ul className="space-y-3 text-sm text-white/60">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                Make sure you have a stable internet connection
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                Test your microphone and camera before the session starts
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                Find a quiet place to minimize background noise
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                Have your questions ready for the Q&A portion
              </li>
            </ul>
          </motion.div>
        )}
      </div>
    </div>
  );
}

